import { Injectable, Output, EventEmitter } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxSoundsService } from "../../bfx-common/services/bfx-sounds.service";
import { TranslateService } from "@ngx-translate/core";
import {
  BlotterFilterPromiseLookupService,
  ActiveUserProfilePromiseService,
  ProfilePromiseActionService,
} from "shared/cubiq-services";
import { ActiveUser, ProfileSettingKeys } from "shared/cubiq-services";
import { Subject, Observable, startWith } from "rxjs";
import { BfxAppEventSetting } from "../../bfx-user-settings/model/bfx-app-event-setting";
import { BfxBlotterFilterService } from "../../bfx-blotter-filter/bfx-blotter-filter.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BFXAppNotificationType } from "../../../shared/cubiq-services/model/core/notifications/BFXAppNotificationType";
import { NONE_FILTER } from "app/bfx-user-settings/model/bfx-settings-constants";

export const PROFILE_KEY: string = ProfileSettingKeys.userProfileSettingsNotifApp;
export const PROFILE_KEY_BMARK: string = ProfileSettingKeys.userProfileSettingsNotifBmarkApp;

@Injectable()
export class BfxAppNotificatonSettingsService {
  private primePromise: Promise<boolean>;
  private primed: boolean = false;

  private activeUser: ActiveUser;

  private existingFilters: Set<string> = new Set();
  private appNotificationSettings: Array<BfxAppEventSetting> = [];
  private appNotifSettingsUpdateSubject: Subject<Array<BfxAppEventSetting>> = new Subject();
  private bmarkAppNotificationSettings: BfxAppEventSetting[] = [];
  private bmarkAppNotifSettingsUpdateSubject: Subject<Array<BfxAppEventSetting>> = new Subject();

  constructor(
    private activeUserService: ActiveUserProfilePromiseService,
    protected filterService: BlotterFilterPromiseLookupService,
    private soundsService: BfxSoundsService,
    private translationService: TranslateService,
    private profilePromiseActionService: ProfilePromiseActionService,
    private blotterFilterilterService: BfxBlotterFilterService,
  ) {
    this.primePromise = new Promise<boolean>((resolve, reject) => {
      this.activeUserService.getActiveUserData().then(
        (data: ActiveUser) => {
          this.activeUser = data;
          this.filterService.getAll().then((filters) => {
            filters.forEach((filter) => this.existingFilters.add(filter.filterId.toString()));
            this.initializeNotificationSettings();
            this.blotterFilterilterService.listenForFilterDeleting().subscribe((ids) => {
              this.removeDeletedFilters(ids);
            });
            this.primed = true;
            resolve(true);
          });
        },
        (rjc) => {
          reject(rjc);
        },
      );
    });
  }

  public getAppNotificationsSettings(): Promise<Array<BfxAppEventSetting>> {
    return new Promise<Array<BfxAppEventSetting>>((res, rej) => {
      this.primePromise.then(
        (value: boolean) => {
          if (value) {
            res(this.appNotificationSettings);
          } else {
            rej([]);
          }
        },
        (rjc) => {
          rej(rjc);
        },
      );
    });
  }

  public getBmarkAppNotificationsSettings(): Promise<Array<BfxAppEventSetting>> {
    return new Promise<Array<BfxAppEventSetting>>((res, rej) => {
      this.primePromise.then(
        (value: boolean) => {
          if (value) {
            res(this.bmarkAppNotificationSettings);
          } else {
            rej([]);
          }
        },
        (rjc) => {
          rej(rjc);
        },
      );
    });
  }

  /**
   * Get a stream of non-bmark app settings primed with initial settings and all subsequent updates
   * @returns
   */
  public getAppSettingsStream(): Observable<Array<BfxAppEventSetting>> {
    return this.appNotifSettingsUpdateSubject
      .asObservable()
      .pipe(startWith(this.appNotificationSettings));
  }

  /**
   * Get a stream of bmark app settings primed with initial settings and all subsequent updates
   * @returns
   */
  public getAppBmarkSettingsStream(): Observable<Array<BfxAppEventSetting>> {
    return this.bmarkAppNotifSettingsUpdateSubject
      .asObservable()
      .pipe(startWith(this.bmarkAppNotificationSettings));
  }

  /**
   * Get a stream of non-bmark app settings updates - there is no priming
   * @returns
   */
  public getAppSettingsUpdateStream(): Observable<Array<BfxAppEventSetting>> {
    return this.appNotifSettingsUpdateSubject.asObservable();
  }

  /**
   * Get a stream of bmark app settings updates - there is no priming
   * @returns
   */
  public getAppBmarkSettingsUpdateStream(): Observable<Array<BfxAppEventSetting>> {
    return this.bmarkAppNotifSettingsUpdateSubject.asObservable();
  }

  public getAllAppNotificationSettings(): Array<BfxAppEventSetting> {
    let allNotifSettings: Array<BfxAppEventSetting> = new Array<BfxAppEventSetting>();
    if (Array.isArray(this.appNotificationSettings)) {
      allNotifSettings = allNotifSettings.concat(this.appNotificationSettings);
    }
    if (Array.isArray(this.bmarkAppNotificationSettings)) {
      allNotifSettings = allNotifSettings.concat(this.bmarkAppNotificationSettings);
    }
    return allNotifSettings;
  }

  public listenForSettingsUpdates(): Observable<Array<BfxAppEventSetting>> {
    return this.appNotifSettingsUpdateSubject.asObservable();
  }

  public listenForBmarkSettingsUpdates(): Observable<Array<BfxAppEventSetting>> {
    return this.bmarkAppNotifSettingsUpdateSubject.asObservable();
  }

  // load the app notification settings, if they are not saved to the profile, we need to load a default per user, that last part still needs to be completed
  private initializeNotificationSettings(): void {
    //let notifEventsList: Array<string> = Object.keys(BFXAppNotificationType).filter(key => !isNaN(Number(BFXAppNotificationType[key])));
    let bmarkPopupNotificationSettingString: string = "";

    this.appNotificationSettings = [];
    if (this.activeUser.profileSettings && this.activeUser.profileSettings.has(PROFILE_KEY)) {
      let settingString: string = this.activeUser.profileSettings.get(PROFILE_KEY);
      if (settingString.length != 0) {
        this.appNotificationSettings = this.parseJsonSetting(settingString);
        this.migrateAlgoSettings(this.appNotificationSettings);
      }
    }
    const defaultSettings = this.getDefaultEventSettings();
    if (this.appNotificationSettings.length == 0) {
      this.appNotificationSettings = defaultSettings.concat();
    }

    this.appNotificationSettings = this.checkAndUpdateNotificationSettings(
      this.appNotificationSettings,
      defaultSettings,
    );

    this.profilePromiseActionService.getProfileConfigUpdates(PROFILE_KEY).subscribe((conf) => {
      this.appNotificationSettings = this.parseJsonSetting(conf);
      this.appNotifSettingsUpdateSubject.next(this.appNotificationSettings);
    });

    // Bmark notification settings
    if (this.activeUser.profileSettings && this.activeUser.profileSettings.has(PROFILE_KEY_BMARK)) {
      bmarkPopupNotificationSettingString = this.activeUser.profileSettings.get(PROFILE_KEY_BMARK);
    }
    if (bmarkPopupNotificationSettingString.length != 0) {
      let bmarkAppNotificationSettings = this.parseJsonSetting(bmarkPopupNotificationSettingString);
      this.bmarkAppNotificationSettings =
        bmarkAppNotificationSettings.length !== 0
          ? bmarkAppNotificationSettings
          : this.getDefaultBmarkEventSettings();
    } else {
      this.bmarkAppNotificationSettings = this.getDefaultBmarkEventSettings();
    }

    this.bmarkAppNotificationSettings = this.checkAndUpdateNotificationSettings(
      this.bmarkAppNotificationSettings,
      this.getDefaultBmarkEventSettings(),
    );

    this.profilePromiseActionService
      .getProfileConfigUpdates(PROFILE_KEY_BMARK)
      .subscribe((conf) => {
        this.bmarkAppNotificationSettings = this.parseJsonSetting(conf);
        this.bmarkAppNotifSettingsUpdateSubject.next(this.bmarkAppNotificationSettings);
      });

    this.appNotifSettingsUpdateSubject.next(this.appNotificationSettings);
    this.bmarkAppNotifSettingsUpdateSubject.next(this.bmarkAppNotificationSettings);
  }

  /**
   * Function for comparing the app notification settings lists, the one saved in the profile and the default in the system.
   * Returns the correct app notification settings list if the default is updated in the future.
   * @param savedAppNotificationList of type `Array<BfxAppEventSetting>`
   * @param defaultAppNotificationSettingsList of type `Array<BfxAppEventSetting>`
   */
  private checkAndUpdateNotificationSettings(
    savedNotificationSettings: Array<BfxAppEventSetting>,
    defaultNotificationSettings: Array<BfxAppEventSetting>,
  ): Array<BfxAppEventSetting> {
    let settings: BfxAppEventSetting[] = [];
    let savedSettingsMap: any = {};
    let filledMigrate = true;
    let filledSavedSetting = null;

    savedNotificationSettings.forEach((setting: BfxAppEventSetting) => {
      savedSettingsMap[setting.type] = setting;

      if (setting.type === BFXAppNotificationType.PART_FILLED) {
        filledMigrate = false;
      }
      if (setting.type === BFXAppNotificationType.FILLED) {
        filledSavedSetting = setting;
      }
    });

    if (filledMigrate) {
      savedSettingsMap[BFXAppNotificationType.PART_FILLED] = filledSavedSetting;
    }

    for (let i: number = 0; i < defaultNotificationSettings.length; i++) {
      let setting: BfxAppEventSetting = defaultNotificationSettings[i];

      if (savedSettingsMap[setting.type] != null) {
        let savedSetting: BfxAppEventSetting = savedSettingsMap[setting.type];
        setting.enabled = savedSetting.enabled;
        setting.audio = savedSetting.audio;
        setting.disableAllAudio = savedSetting.disableAllAudio;
        setting.filter = savedSetting.filter;
        setting.returnOnLogin = savedSetting.returnOnLogin;
      }

      settings.push(setting);
    }
    settings.sort((s1, s2) => s1.eventIdentifier.localeCompare(s2.eventIdentifier));
    return settings;
  }

  private parseJsonSetting(json: string): Array<any> {
    try {
      let result = [];
      let settingList: any = [];
      settingList = JSON.parse(json);
      if (settingList && settingList.length != 0) {
        settingList.forEach((event) => {
          if (event.returnOnLogin == undefined)
            event.returnOnLogin = this.getReturnOnLogin(event.type);
          let eventObj: BfxAppEventSetting = new BfxAppEventSetting(
            event.type,
            event.eventIdentifier,
            event.enabled,
            event.returnOnLogin,
            event.audio,
            event.disableAllAudio,
            event.filter,
            this.getBlotterType(event.type),
            event.isBmark,
          );
          this.checkFilterExist(eventObj);
          result.push(eventObj);
        });
        return result;
      }
    } catch (error) {
      logger.out.error("Error parsing app settings: " + error);
    }
    return [];
  }

  /**
   * If we load settings that do not have new algo settings included, then create
   * new algo settings based on their LIMIT base settings
   * @param appSettings
   */
  private migrateAlgoSettings(appSettings: BfxAppEventSetting[]) {
    if (!appSettings.find((setting) => setting.type === BFXAppNotificationType.ALGO_ACTIVATED)) {
      // WE don't have the marker Algo setting so add all missing
      // Need to migrate algo settings from existing settings
      const appMap: Map<BFXAppNotificationType, BfxAppEventSetting> = new Map();
      appSettings.forEach((setting) => appMap.set(setting.type, setting));
      // List of object specifying the new algoType and the corresponding base setting type it inherits from
      const toMigrate = [
        {
          algoType: BFXAppNotificationType.ALGO_ACTIVATED,
          baseType: BFXAppNotificationType.ACTIVATED,
        },
        {
          algoType: BFXAppNotificationType.ALGO_CANCELLED,
          baseType: BFXAppNotificationType.CANCELLED,
        },
        { algoType: BFXAppNotificationType.ALGO_ENTERED, baseType: BFXAppNotificationType.ENTERED },
        { algoType: BFXAppNotificationType.ALGO_EXPIRED, baseType: BFXAppNotificationType.EXPIRED },
        { algoType: BFXAppNotificationType.ALGO_FILLED, baseType: BFXAppNotificationType.FILLED },
        {
          algoType: BFXAppNotificationType.ALGO_MODIFIED,
          baseType: BFXAppNotificationType.MODIFIED,
        },
        {
          algoType: BFXAppNotificationType.ALGO_PART_FILLED,
          baseType: BFXAppNotificationType.PART_FILLED,
        },
      ];
      for (const { algoType, baseType } of toMigrate) {
        if (!appMap.has(algoType)) {
          // Create algo default from specified setting with the base type
          const baseSetting = appMap.get(baseType);
          if (baseSetting) {
            const newSetting = Object.assign({}, baseSetting, { type: algoType });
            appSettings.push(newSetting);
          }
        }
      }
    }
  }

  private getDefaultEventSettings(): Array<BfxAppEventSetting> {
    return [
      this.createDefaultEventSetting(BFXAppNotificationType.ACTIVATED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_ACTIVATED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_CANCELLED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_ENTERED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_EXPIRED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_FILLED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_MODIFIED),
      this.createDefaultEventSetting(BFXAppNotificationType.ALGO_PART_FILLED),
      this.createDefaultEventSetting(BFXAppNotificationType.CANCELLED),
      this.createDefaultEventSetting(BFXAppNotificationType.ENTERED),
      this.createDefaultEventSetting(BFXAppNotificationType.EXPIRED),
      this.createDefaultEventSetting(BFXAppNotificationType.FILLED),
      this.createDefaultEventSetting(BFXAppNotificationType.MODIFIED),
      this.createDefaultEventSetting(BFXAppNotificationType.PART_FILLED),
      this.createDefaultEventSetting(BFXAppNotificationType.SUSPENDED),
    ];
  }

  private getDefaultBmarkEventSettings(): Array<BfxAppEventSetting> {
    return [
      // this.createDefaultAppNotificationSetting(BFXAppNotificationType.APP_BMARK_NEW_ORDERS),
      this.createDefaultAppNotificationSetting(BFXAppNotificationType.APP_BMARK_CANCELLED),
      this.createDefaultAppNotificationSetting(BFXAppNotificationType.APP_BMARK_ENTERED),
      this.createDefaultAppNotificationSetting(BFXAppNotificationType.APP_BMARK_MODIFIED),
      this.createDefaultAppNotificationSetting(BFXAppNotificationType.APP_BMARK_FILLED),
    ];
  }

  private createDefaultEventSetting(type: BFXAppNotificationType) {
    return new BfxAppEventSetting(
      type,
      this.translationService.instant(BFXAppNotificationType[type]),
      this.getIsNotifEnabled(type, this.activeUser.userTypeId),
      this.getReturnOnLogin(type),
      this.getAudio(type, this.activeUser.userTypeId),
      this.getDisableAllAudio(type, this.activeUser.userTypeId),
      this.getFilter(type, this.activeUser.userTypeId),
      this.getBlotterType(type),
    );
  }

  private createDefaultAppNotificationSetting(type: BFXAppNotificationType) {
    return new BfxAppEventSetting(
      type,
      this.translationService.instant(BFXAppNotificationType[type]),
      false, // this.getIsNotifEnabled(type,this.activeUser.userTypeId),
      this.getReturnOnLogin(type),
      this.getAudio(type, this.activeUser.userTypeId), // {label:'None'}, // Audio ->
      this.getDisableAllAudio(type, this.activeUser.userTypeId), // true, //
      { name: "None" }, // Filter -> this.getFilter(type,this.activeUser.userTypeId),
      CubiqTS.BlotterType.BENCHMARK,
      true,
    );
  }

  // get default value for whether notification is on or off by user type and notification type
  private getIsNotifEnabled(type: BFXAppNotificationType, userType: CubiqTS.UserType): boolean {
    switch (userType) {
      case CubiqTS.UserType.TRADER:
      case CubiqTS.UserType.SALES:
      case CubiqTS.UserType.CLIENT:
        return (
          type === BFXAppNotificationType.FILLED ||
          type === BFXAppNotificationType.PART_FILLED ||
          type === BFXAppNotificationType.ALGO_FILLED ||
          type === BFXAppNotificationType.ALGO_PART_FILLED
        );
      case CubiqTS.UserType.ADMIN:
        return false;
    }
    return false;
  }

  private getReturnOnLogin(type: BFXAppNotificationType): boolean {
    switch (type) {
      case BFXAppNotificationType.ALGO_CANCELLED:
      case BFXAppNotificationType.ALGO_ENTERED:
      case BFXAppNotificationType.ALGO_EXPIRED:
      case BFXAppNotificationType.ALGO_FILLED:
      case BFXAppNotificationType.ALGO_PART_FILLED:
      case BFXAppNotificationType.APP_BMARK_CANCELLED:
      case BFXAppNotificationType.APP_BMARK_ENTERED:
      case BFXAppNotificationType.APP_BMARK_FILLED:
      case BFXAppNotificationType.CANCELLED:
      case BFXAppNotificationType.ENTERED:
      case BFXAppNotificationType.EXPIRED:
      case BFXAppNotificationType.FILLED:
      case BFXAppNotificationType.PART_FILLED:
        return false;
    }
    return null;
  }

  private getDisableAllAudio(type: BFXAppNotificationType, userType: CubiqTS.UserType): boolean {
    // Must be constant
    return false;
  }

  // get default value for whether notification is on or off by user type and notification type
  private getAudio(type: BFXAppNotificationType, userType: CubiqTS.UserType) {
    return { label: "None" };
  }

  // get default value for whether notification is on or off by user type and notification type
  private getFilter(_type: BFXAppNotificationType, _userType: CubiqTS.UserType) {
    return NONE_FILTER;
  }

  private getBlotterType(type: BFXAppNotificationType): CubiqTS.BlotterType {
    switch (type) {
      case BFXAppNotificationType.ACTIVATED:
      case BFXAppNotificationType.ALGO_ACTIVATED:
      case BFXAppNotificationType.ALGO_CANCELLED:
      case BFXAppNotificationType.ALGO_ENTERED:
      case BFXAppNotificationType.ALGO_EXPIRED:
      case BFXAppNotificationType.ALGO_MODIFIED:
      case BFXAppNotificationType.CANCELLED:
      case BFXAppNotificationType.ENTERED:
      case BFXAppNotificationType.EXPIRED:
      case BFXAppNotificationType.MODIFIED:
      case BFXAppNotificationType.SUSPENDED:
        return CubiqTS.BlotterType.ORDER;
      case BFXAppNotificationType.PART_FILLED:
      case BFXAppNotificationType.FILLED:
      case BFXAppNotificationType.ALGO_PART_FILLED:
      case BFXAppNotificationType.ALGO_FILLED:
        return CubiqTS.BlotterType.TRADE;
    }
    return CubiqTS.BlotterType.ORDER;
  }

  private removeDeletedFilters(ids: string[]) {
    ids.forEach((filterId) => {
      this.existingFilters.delete(filterId);
      let popupEventObject: BfxAppEventSetting = this.appNotificationSettings.find((a) => {
        return a.filter.id == filterId;
      });
      if (popupEventObject !== undefined)
        popupEventObject.filter = { name: "None", id: "0", blotterType: 0 };
    });
  }
  private checkFilterExist(eventObj: BfxAppEventSetting) {
    if (!this.existingFilters.has(eventObj.filter.id)) {
      eventObj.filter = { name: "None", id: "0", blotterType: 0 };
    }
  }
}
